import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Clock, 
  Filter, 
  DollarSign, 
  TrendingUp, 
  BarChart3, 
  Eye, 
  CheckCircle, 
  AlertCircle, 
  XCircle,
  Package,
  CreditCard,
  MapPin,
  Phone,
  User,
  Hash,
  Search
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, Order } from '../../services/dataService';

interface OrderFilters {
  dateFrom: string;
  dateTo: string;
  status: 'all' | 'active' | 'completed' | 'cancelled';
  paymentStatus: 'all' | 'paid' | 'unpaid' | 'partial';
  customer: string;
  minAmount: string;
  maxAmount: string;
  tableNumber: string;
  timeFilter: 'today' | 'weekly' | 'monthly' | 'custom';
}

interface OrderAnalytics {
  totalOrders: number;
  totalRevenue: number;
  averageOrderValue: number;
  paidOrders: number;
  unpaidOrders: number;
  partiallyPaidOrders: number;
  ordersByStatus: {
    active: number;
    completed: number;
    cancelled: number;
  };
  topCustomers: { name: string; totalSpent: number; orderCount: number }[];
  popularItems: { name: string; quantity: number; revenue: number }[];
  dailyRevenue: { date: string; revenue: number; orders: number }[];
}

const OrderHistory: React.FC = () => {
  const { tenantData } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [analytics, setAnalytics] = useState<OrderAnalytics>({
    totalOrders: 0,
    totalRevenue: 0,
    averageOrderValue: 0,
    paidOrders: 0,
    unpaidOrders: 0,
    partiallyPaidOrders: 0,
    ordersByStatus: { active: 0, completed: 0, cancelled: 0 },
    topCustomers: [],
    popularItems: [],
    dailyRevenue: []
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'orders' | 'analytics'>('orders');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<OrderFilters>({
    dateFrom: '',
    dateTo: '',
    status: 'all',
    paymentStatus: 'all',
    customer: '',
    minAmount: '',
    maxAmount: '',
    tableNumber: '',
    timeFilter: 'today'
  });

  // Helper function to safely convert date
  const safeToDate = (date: any): Date => {
    if (!date) return new Date();
    if (date instanceof Date) return date;
    if (date.toDate && typeof date.toDate === 'function') return date.toDate();
    if (typeof date === 'string' || typeof date === 'number') return new Date(date);
    return new Date();
  };

  // Helper function to safely format date
  const safeFormatDate = (date: any): string => {
    try {
      const dateObj = safeToDate(date);
      return dateObj.toLocaleDateString();
    } catch (error) {
      return 'Unknown date';
    }
  };

  // Helper function to format date and time
  const formatDateTime = (date: any): string => {
    try {
      const dateObj = safeToDate(date);
      return dateObj.toLocaleString();
    } catch (error) {
      return 'Unknown date';
    }
  };

  const loadData = useCallback(async () => {
    if (!tenantData?.id) return;

    try {
      setLoading(true);
      
      // Load orders
      const ordersData = await dataService.getOrders(tenantData.id);

      setOrders(ordersData);

    } catch (error) {
      console.error('Error loading order history data:', error);
    } finally {
      setLoading(false);
    }
  }, [tenantData]);

  // Apply filters to orders
  useEffect(() => {
    let filtered = [...orders];

    // Time-based filter
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (filters.timeFilter) {
      case 'today':
        filtered = filtered.filter(order => {
          const orderDate = safeToDate(order.createdAt);
          return orderDate >= today;
        });
        break;
      case 'weekly':
        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        filtered = filtered.filter(order => {
          const orderDate = safeToDate(order.createdAt);
          return orderDate >= weekAgo;
        });
        break;
      case 'monthly':
        const monthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
        filtered = filtered.filter(order => {
          const orderDate = safeToDate(order.createdAt);
          return orderDate >= monthAgo;
        });
        break;
      case 'custom':
        // Use existing date range filters
        if (filters.dateFrom) {
          const fromDate = new Date(filters.dateFrom);
          filtered = filtered.filter(order => safeToDate(order.createdAt) >= fromDate);
        }
        if (filters.dateTo) {
          const toDate = new Date(filters.dateTo);
          toDate.setHours(23, 59, 59, 999);
          filtered = filtered.filter(order => safeToDate(order.createdAt) <= toDate);
        }
        break;
    }

    // Status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(order => order.status === filters.status);
    }

    // Payment status filter
    if (filters.paymentStatus !== 'all') {
      filtered = filtered.filter(order => {
        if (filters.paymentStatus === 'paid') return order.isPaid;
        if (filters.paymentStatus === 'unpaid') return !order.isPaid && order.paidAmount === 0;
        if (filters.paymentStatus === 'partial') return !order.isPaid && order.paidAmount > 0;
        return true;
      });
    }

    // Customer filter
    if (filters.customer) {
      filtered = filtered.filter(order => 
        order.customer.toLowerCase().includes(filters.customer.toLowerCase())
      );
    }

    // Amount range filter
    if (filters.minAmount) {
      const minAmount = parseFloat(filters.minAmount);
      filtered = filtered.filter(order => order.total >= minAmount);
    }

    if (filters.maxAmount) {
      const maxAmount = parseFloat(filters.maxAmount);
      filtered = filtered.filter(order => order.total <= maxAmount);
    }

    // Table number filter
    if (filters.tableNumber) {
      filtered = filtered.filter(order => 
        order.tableNumber?.toLowerCase().includes(filters.tableNumber.toLowerCase())
      );
    }

    setFilteredOrders(filtered);
  }, [orders, filters]);

  // Calculate analytics
  useEffect(() => {
    if (filteredOrders.length === 0) {
      setAnalytics({
        totalOrders: 0,
        totalRevenue: 0,
        averageOrderValue: 0,
        paidOrders: 0,
        unpaidOrders: 0,
        partiallyPaidOrders: 0,
        ordersByStatus: { active: 0, completed: 0, cancelled: 0 },
        topCustomers: [],
        popularItems: [],
        dailyRevenue: []
      });
      return;
    }

    const totalOrders = filteredOrders.length;
    const totalRevenue = filteredOrders.reduce((sum, order) => sum + (order.total || 0), 0);
    const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    const paidOrders = filteredOrders.filter(order => order.isPaid).length;
    const unpaidOrders = filteredOrders.filter(order => !order.isPaid && (order.paidAmount || 0) === 0).length;
    const partiallyPaidOrders = filteredOrders.filter(order => !order.isPaid && (order.paidAmount || 0) > 0).length;

    const ordersByStatus = {
      active: filteredOrders.filter(order => order.status === 'active').length,
      completed: filteredOrders.filter(order => order.status === 'completed').length,
      cancelled: filteredOrders.filter(order => order.status === 'cancelled').length
    };

    // Calculate top customers
    const customerMap = new Map<string, { totalSpent: number; orderCount: number }>();
    filteredOrders.forEach(order => {
      const existing = customerMap.get(order.customer) || { totalSpent: 0, orderCount: 0 };
      customerMap.set(order.customer, {
        totalSpent: existing.totalSpent + (order.total || 0),
        orderCount: existing.orderCount + 1
      });
    });

    const topCustomers = Array.from(customerMap.entries())
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.totalSpent - a.totalSpent)
      .slice(0, 5);

    // Calculate popular items
    const itemMap = new Map<string, { quantity: number; revenue: number }>();
    filteredOrders.forEach(order => {
      (order.items || []).forEach(item => {
        const existing = itemMap.get(item.menuItemName) || { quantity: 0, revenue: 0 };
        itemMap.set(item.menuItemName, {
          quantity: existing.quantity + (item.quantity || 0),
          revenue: existing.revenue + (item.totalPrice || 0)
        });
      });
    });

    const popularItems = Array.from(itemMap.entries())
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 5);

    // Calculate daily revenue for the filtered period
    const dailyRevenueMap = new Map<string, { revenue: number; orders: number }>();
    filteredOrders.forEach(order => {
      const date = safeToDate(order.createdAt).toISOString().split('T')[0];
      const existing = dailyRevenueMap.get(date) || { revenue: 0, orders: 0 };
      dailyRevenueMap.set(date, {
        revenue: existing.revenue + (order.total || 0),
        orders: existing.orders + 1
      });
    });

    const dailyRevenue = Array.from(dailyRevenueMap.entries())
      .map(([date, data]) => ({ date, ...data }))
      .sort((a, b) => a.date.localeCompare(b.date));

    setAnalytics({
      totalOrders,
      totalRevenue,
      averageOrderValue,
      paidOrders,
      unpaidOrders,
      partiallyPaidOrders,
      ordersByStatus,
      topCustomers,
      popularItems,
      dailyRevenue
    });
  }, [filteredOrders]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const clearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      status: 'all',
      paymentStatus: 'all',
      customer: '',
      minAmount: '',
      maxAmount: '',
      tableNumber: '',
      timeFilter: 'today'
    });
  };

  const getStatusIcon = (status: string) => {
    switch ((status || '').toLowerCase()) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'active':
        return <AlertCircle className="w-4 h-4 text-primary-600" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Package className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch ((status || '').toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'active':
        return 'bg-primary-100 text-primary-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusColor = (order: Order) => {
    if (order.isPaid) return 'bg-green-100 text-green-800';
    if (order.paidAmount > 0) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getPaymentStatusText = (order: Order) => {
    if (order.isPaid) return 'Paid';
    if (order.paidAmount > 0) return 'Partial';
    return 'Unpaid';
  };

  const exportOrdersToCSV = useCallback(() => {
    if (filteredOrders.length === 0) {
      alert('No orders to export. Please adjust your filters.');
      return;
    }

    // Define CSV headers
    const headers = [
      'Order ID',
      'Date',
      'Time',
      'Customer Name',
      'Customer Phone',
      'Table Number',
      'Items',
      'Item Details',
      'Total Amount',
      'Paid Amount',
      'Order Status',
      'Payment Status'
    ];

    // Convert orders to CSV rows
    const csvRows = filteredOrders.map(order => {
      const orderDate = safeToDate(order.createdAt);
      const itemsCount = order.items?.length || 0;
      const itemDetails = (order.items || []).map(item => 
        `${item.menuItemName} (${item.quantity}x ₹${item.unitPrice})`
      ).join('; ');

      return [
        order.id || 'N/A',
        orderDate.toLocaleDateString(),
        orderDate.toLocaleTimeString(),
        order.customer,
        order.customerPhone || 'N/A',
        order.tableNumber || 'N/A',
        itemsCount,
        itemDetails,
        (order.total || 0).toFixed(2),
        (order.paidAmount || 0).toFixed(2),
        order.status,
        getPaymentStatusText(order)
      ];
    });

    // Combine headers and rows
    const csvContent = [headers, ...csvRows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      // Generate filename with current date and filter info
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      const filterInfo = filters.dateFrom && filters.dateTo 
        ? `_${filters.dateFrom}_to_${filters.dateTo}`
        : filters.dateFrom 
        ? `_from_${filters.dateFrom}`
        : filters.dateTo
        ? `_to_${filters.dateTo}`
        : '';
      
      link.setAttribute('download', `order_history_${dateStr}${filterInfo}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [filteredOrders, filters, getPaymentStatusText, safeToDate]);

  // Listen for navbar actions
  useEffect(() => {
    const handleNavbarAction = (event: CustomEvent) => {
      const action = event.detail;
      if (action === 'export-orders') {
        exportOrdersToCSV();
      }
    };

    window.addEventListener('navbar-action', handleNavbarAction as EventListener);
    
    return () => {
      window.removeEventListener('navbar-action', handleNavbarAction as EventListener);
    };
  }, [exportOrdersToCSV]); // Add exportOrdersToCSV as dependency

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-2 sm:p-4 md:p-6 lg:p-8">
      {/* Header Section */}
      <div className="mb-4 sm:mb-8 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-2 sm:gap-0">
          <div className="animate-slide-in-left">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
              Order History
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-base sm:text-lg">
              {filteredOrders.length} orders • ₹{analytics.totalRevenue.toFixed(2)} total revenue
            </p>
          </div>
        </div>
        {/* Last Updated */}
        <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-lg px-3 py-1 sm:px-4 sm:py-2 inline-block">
          Last updated: {new Date().toLocaleTimeString()} • Real-time updates enabled
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6 sm:mb-8">
        {/* Total Orders */}
        <div className="group cursor-pointer bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-2xl transition-all duration-700 ease-out hover:-translate-y-2 sm:hover:-translate-y-4 hover:scale-105 animate-fade-in-up focus:outline-none focus:ring-2 focus:ring-primary-500">
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
              <Package className="w-4 h-4 sm:w-6 sm:h-6 text-white group-hover:scale-110 transition-transform duration-300" />
            </div>
          </div>
          <div>
                            <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300">Total Orders</p>
                <p className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2 group-hover:text-primary-700 dark:group-hover:text-primary-300 transition-colors duration-300">{analytics.totalOrders}</p>
          </div>
        </div>
        {/* Total Revenue */}
        <div className="group cursor-pointer bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-2xl transition-all duration-700 ease-out hover:-translate-y-2 sm:hover:-translate-y-4 hover:scale-105 animate-fade-in-up animation-delay-100 focus:outline-none focus:ring-2 focus:ring-primary-500">
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
              <DollarSign className="w-4 h-4 sm:w-6 sm:h-6 text-white group-hover:scale-110 transition-transform duration-300" />
            </div>
          </div>
          <div>
            <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1 group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors duration-300">Total Revenue</p>
            <p className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2 group-hover:text-green-700 dark:group-hover:text-green-300 transition-colors duration-300">₹{analytics.totalRevenue.toFixed(2)}</p>
          </div>
        </div>
        {/* Average Order Value */}
        <div className="group cursor-pointer bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-2xl transition-all duration-700 ease-out hover:-translate-y-2 sm:hover:-translate-y-4 hover:scale-105 animate-fade-in-up animation-delay-200 focus:outline-none focus:ring-2 focus:ring-primary-500">
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
              <TrendingUp className="w-4 h-4 sm:w-6 sm:h-6 text-white group-hover:scale-110 transition-transform duration-300" />
            </div>
          </div>
          <div>
            <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors duration-300">Avg Order Value</p>
            <p className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2 group-hover:text-indigo-700 dark:group-hover:text-indigo-300 transition-colors duration-300">₹{analytics.averageOrderValue.toFixed(2)}</p>
          </div>
        </div>
        {/* Paid Orders */}
        <div className="group cursor-pointer bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-2xl transition-all duration-700 ease-out hover:-translate-y-2 sm:hover:-translate-y-4 hover:scale-105 animate-fade-in-up animation-delay-300 focus:outline-none focus:ring-2 focus:ring-primary-500">
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
              <CreditCard className="w-4 h-4 sm:w-6 sm:h-6 text-white group-hover:scale-110 transition-transform duration-300" />
            </div>
          </div>
          <div>
            <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-300">Paid Orders</p>
            <p className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2 group-hover:text-purple-700 dark:group-hover:text-purple-300 transition-colors duration-300">{analytics.paidOrders}</p>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">From Date</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">To Date</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Order Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value as any }))}
                className="input-field"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Payment Status</label>
              <select
                value={filters.paymentStatus}
                onChange={(e) => setFilters(prev => ({ ...prev, paymentStatus: e.target.value as any }))}
                className="input-field"
              >
                <option value="all">All Payment Status</option>
                <option value="paid">Paid</option>
                <option value="unpaid">Unpaid</option>
                <option value="partial">Partially Paid</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Customer</label>
              <input
                type="text"
                placeholder="Search customer..."
                value={filters.customer}
                onChange={(e) => setFilters(prev => ({ ...prev, customer: e.target.value }))}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Min Amount</label>
              <input
                type="number"
                placeholder="₹0"
                value={filters.minAmount}
                onChange={(e) => setFilters(prev => ({ ...prev, minAmount: e.target.value }))}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Max Amount</label>
              <input
                type="number"
                placeholder="₹999999"
                value={filters.maxAmount}
                onChange={(e) => setFilters(prev => ({ ...prev, maxAmount: e.target.value }))}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Table Number</label>
              <input
                type="text"
                placeholder="Table..."
                value={filters.tableNumber}
                onChange={(e) => setFilters(prev => ({ ...prev, tableNumber: e.target.value }))}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Time Range</label>
              <select
                value={filters.timeFilter}
                onChange={(e) => setFilters(prev => ({ ...prev, timeFilter: e.target.value as any }))}
                className="input-field"
              >
                <option value="today">Today</option>
                <option value="weekly">Last 7 Days</option>
                <option value="monthly">Last 30 Days</option>
                <option value="custom">Custom Date Range</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-4">
            <button
              onClick={clearFilters}
              className="btn-secondary"
            >
              Clear Filters
            </button>
            <button
              onClick={() => setShowFilters(false)}
              className="btn-primary"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto">
          <div className="p-2 sm:p-4 md:p-6 lg:p-8">
            {activeTab === 'orders' && (
              <div className="space-y-4">
                {/* Filter Bar */}
                <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                    {/* Time Filter */}
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setFilters(prev => ({ ...prev, timeFilter: 'today' }))}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                          filters.timeFilter === 'today'
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        Today
                      </button>
                      <button
                        onClick={() => setFilters(prev => ({ ...prev, timeFilter: 'weekly' }))}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                          filters.timeFilter === 'weekly'
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        Weekly
                      </button>
                      <button
                        onClick={() => setFilters(prev => ({ ...prev, timeFilter: 'monthly' }))}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                          filters.timeFilter === 'monthly'
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        Monthly
                      </button>
                      <button
                        onClick={() => setFilters(prev => ({ ...prev, timeFilter: 'custom' }))}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                          filters.timeFilter === 'custom'
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        Custom
                      </button>
                    </div>

                    {/* Search and Additional Filters */}
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search orders..."
                          value={filters.customer}
                          onChange={(e) => setFilters(prev => ({ ...prev, customer: e.target.value }))}
                          className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-sm"
                        />
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
                      </div>
                      <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`p-2 rounded-lg transition-colors ${
                          showFilters
                            ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                        title="Advanced Filters"
                      >
                        <Filter className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Results Count */}
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Showing {filteredOrders.length} of {orders.length} orders
                    </p>
                  </div>
                </div>

                {/* Custom Date Range (shown when custom is selected) */}
                {filters.timeFilter === 'custom' && (
                  <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">From Date</label>
                        <input
                          type="date"
                          value={filters.dateFrom}
                          onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">To Date</label>
                        <input
                          type="date"
                          value={filters.dateTo}
                          onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Orders List */}
                <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Orders</h3>
                  </div>

                  {filteredOrders.length === 0 ? (
                    <div className="text-center py-12">
                      <Package className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                      <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">No orders found</h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        {Object.values(filters).some(value => value !== '' && value !== 'all')
                          ? 'Try adjusting your search criteria.'
                          : 'No orders have been placed yet.'
                        }
                      </p>
                    </div>
                  ) : (
                    <div className="p-4 sm:p-6 space-y-3">
                      {filteredOrders.map((order) => (
                        <div
                          key={order.id}
                          className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 hover:shadow-lg hover:scale-[1.02] transition-all duration-300"
                        >
                          {/* Order Header */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-2">
                              <Hash className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                              <span className="text-lg font-semibold text-gray-900 dark:text-white">
                                Order #{order.id || 'N/A'}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              {getStatusIcon(order.status)}
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>
                                {order.status}
                              </span>
                            </div>
                          </div>

                          {/* Order Details */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                            {/* Customer Info */}
                            <div className="space-y-1">
                              <div className="flex items-center space-x-2">
                                <User className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                  {order.customer}
                                </span>
                              </div>
                              {order.customerPhone && (
                                <div className="flex items-center space-x-1 text-sm text-gray-500 dark:text-gray-400 ml-6">
                                  <Phone className="w-3 h-3" />
                                  <span>{order.customerPhone}</span>
                                </div>
                              )}
                            </div>

                            {/* Items */}
                            <div className="space-y-1">
                              <div className="text-sm text-gray-900 dark:text-white">
                                {order?.items?.length} item{order?.items?.length !== 1 ? 's' : ''}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {order?.items?.slice(0, 2).map(item => item.menuItemName).join(', ')}
                                {order?.items && order.items.length > 2 && '...'}
                              </div>
                            </div>

                            {/* Amount */}
                            <div className="space-y-1">
                              <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                ₹{(order.total || 0).toFixed(2)}
                              </div>
                              {(order.paidAmount || 0) > 0 && (order.paidAmount || 0) < (order.total || 0) && (
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  Paid: ₹{(order.paidAmount || 0).toFixed(2)}
                                </div>
                              )}
                            </div>

                            {/* Payment Status */}
                            <div className="space-y-1">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPaymentStatusColor(order)}`}>
                                {getPaymentStatusText(order)}
                              </span>
                            </div>
                          </div>

                          {/* Order Footer */}
                          <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                            <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                              <div className="flex items-center space-x-1">
                                <Clock className="w-3 h-3" />
                                <span>{formatDateTime(order.createdAt)}</span>
                              </div>
                              {order.tableNumber && (
                                <div className="flex items-center space-x-1">
                                  <MapPin className="w-3 h-3" />
                                  <span>Table {order.tableNumber}</span>
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() => navigate(`/orders/${order.id}`)}
                              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-800/50 transition-colors"
                            >
                              <Eye className="w-4 h-4" />
                              <span>View Details</span>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'analytics' && (
              <div className="space-y-6">
                {/* Analytics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Completed Orders</p>
                        <p className="text-2xl font-bold text-green-600 dark:text-green-400">{analytics.ordersByStatus.completed}</p>
                      </div>
                      <CheckCircle className="w-8 h-8 text-green-500 dark:text-green-400" />
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Orders</p>
                        <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">{analytics.ordersByStatus.active}</p>
                      </div>
                                              <AlertCircle className="w-8 h-8 text-primary-500 dark:text-primary-400" />
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Cancelled Orders</p>
                        <p className="text-2xl font-bold text-red-600 dark:text-red-400">{analytics.ordersByStatus.cancelled}</p>
                      </div>
                      <XCircle className="w-8 h-8 text-red-500 dark:text-red-400" />
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Unpaid Orders</p>
                        <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{analytics.unpaidOrders}</p>
                      </div>
                      <AlertCircle className="w-8 h-8 text-orange-500 dark:text-orange-400" />
                    </div>
                  </div>
                </div>

                {/* Top Customers and Popular Items */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Top Customers */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Top Customers</h3>
                    <div className="space-y-4">
                      {analytics.topCustomers.map((customer, index) => (
                        <div key={customer.name} className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0 h-8 w-8 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                              <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                                {index + 1}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">{customer.name}</p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">{customer.orderCount} orders</p>
                            </div>
                          </div>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">₹{customer.totalSpent.toFixed(2)}</p>
                        </div>
                      ))}
                      {analytics.topCustomers.length === 0 && (
                        <p className="text-gray-500 dark:text-gray-400 text-center py-4">No customer data available</p>
                      )}
                    </div>
                  </div>

                  {/* Popular Items */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Popular Items</h3>
                    <div className="space-y-4">
                      {analytics.popularItems.map((item, index) => (
                        <div key={item.name} className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0 h-8 w-8 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                              <span className="text-sm font-medium text-orange-600 dark:text-orange-400">
                                {index + 1}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">{item.name}</p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">{item.quantity} sold</p>
                            </div>
                          </div>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">₹{item.revenue.toFixed(2)}</p>
                        </div>
                      ))}
                      {analytics.popularItems.length === 0 && (
                        <p className="text-gray-500 dark:text-gray-400 text-center py-4">No item data available</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Daily Revenue Chart Placeholder */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Daily Revenue Trend</h3>
                  <div className="h-64 flex items-center justify-center bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="text-center">
                      <BarChart3 className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
                      <p className="text-gray-500 dark:text-gray-400">Revenue chart visualization coming soon</p>
                      <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                        {analytics.dailyRevenue.length} days of data available
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderHistory; 